import { useState, useEffect } from 'react';

import useIsScreenSize from 'js/hooks/use-is-screen-size';
import { SearchQueryParameter } from 'js/hooks/use-fetch-filtered-results';

import Button from 'components/button/Button';
import Icon from 'components/icon/Icon';
import Checkbox from 'components/form-elements/checkbox/Checkbox';
import RangeSlider from 'components/range-slider/RangeSlider';
import Spinner from 'components/spinner/Spinner';

import FilterGroup from './FilterGroup';

import { ICheckbox, IFilters } from 'source/NFR.types';

interface IFiltersProps extends IFilters {
  close: () => void;
  fetchFilteredResults: (searchQueryParameter: SearchQueryParameter) => void;
  isLoading?: boolean;
  updateFilterQuery?: (field: Record<string, (string | number)[]>) => void;
  dataId?: number;
}

const Filters: React.FC<IFiltersProps> = ({
  checkboxes = [],
  items = [],
  labels,
  rangeFilter,
  fetchFilteredResults,
  title,
  close,
  mobileTitle,
  isLoading,
  dataId,
  updateFilterQuery
}) => {
  const [isResetting, setIsResetting] = useState<boolean>(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [searchQueryParameter, setSearchQueryParameter] = useState<Record<string, (string | number)[]>>(() => ({
    ...getInitialSearchQueryParameter(items),
    ...getInitialSearchQueryParameter([{ options: checkboxes }])
  }));
  const isDesktop = useIsScreenSize('lg');

  useEffect(() => {
    if (!updateFilterQuery) return;
    updateFilterQuery(searchQueryParameter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQueryParameter]);
  // updateFilterQuery is a prop callback - adding causes re-renders

  const setSearchQueryParameterHelper = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setSearchQueryParameter((prevState: Record<string, (string | number)[]>) => {
      const currentValues = prevState[name] || [];
      return {
        ...prevState,
        [name]: checked ? [...currentValues, value] : currentValues.filter((item: string | number) => item !== value)
      };
    });
  };

  function getInitialSearchQueryParameter(items: { options: ICheckbox[] }[]): Record<string, (string | number)[]> {
    const result: Record<string, (string | number)[]> = {};
    items?.forEach(item => {
      item.options.forEach(option => {
        const target = option.name;
        if (!(target in result)) {
          result[target] = [];
        }
        if (option.checked) {
          result[target] = result[target].concat(option.value);
        }
      });
    });
    return result;
  }

  useEffect(() => {
    if (isMounted) {
      // Convert to SearchQueryParameter format (string | string[])
      const queryParams: SearchQueryParameter = Object.entries(searchQueryParameter).reduce<SearchQueryParameter>(
        (acc, [key, values]) => ({
          ...acc,
          [key]: values.map(String)
        }),
        {}
      );
      fetchFilteredResults(queryParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQueryParameter]);
  // fetchFilteredResults is a prop callback - adding causes re-renders

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isLoading && isResetting) {
      setIsResetting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const deselectItems = () => {
    setIsResetting(true);
    setSearchQueryParameter((state: Record<string, (string | number)[]>) =>
      Object.keys(state).reduce<Record<string, (string | number)[]>>(
        (acc, key) => ({
          ...acc,
          [key]: []
        }),
        {}
      )
    );
  };

  return (
    <div id="filters" className="filters">
      {!isDesktop ? (
        <div className="filters__mobile-header">
          <span className="filters__mobile-title">{mobileTitle}</span>
          <button className="filters__mobile-close" onClick={() => close()} type="button">
            <span className="filters__mobile-close-text">{labels.closeDialog}</span>
            <span aria-hidden="true" className="filters__mobile-close-icon">
              <Icon name="x-rounded" />
            </span>
          </button>
        </div>
      ) : (
        title && <div className="filters__title">{title}</div>
      )}
      <div className="filters__filters">
        {items?.map(item => (
          <FilterGroup
            {...item}
            key={item.title}
            updateFilterOptions={setSearchQueryParameterHelper}
            isDesktop={!!isDesktop}
            dataId={dataId}
          />
        ))}
        {checkboxes?.map(checkbox => (
          <Checkbox
            className="filters__checkbox"
            key={checkbox.name}
            onChange={setSearchQueryParameterHelper}
            dataId={dataId}
            {...checkbox}
          />
        ))}
        <button type="button" className="filters__deselect-button" onClick={deselectItems}>
          {labels.reset}
        </button>
        {rangeFilter && <RangeSlider className="filters__range" {...rangeFilter} />}
      </div>
      {!isDesktop && labels.showResults && (
        <div className="filters__show-results">
          <Button onClick={close} theme={['primary', 'large']}>
            {labels.showResults}
          </Button>
        </div>
      )}

      <Spinner isActive={!!isResetting} />
    </div>
  );
};

Filters.displayName = 'Filters';

export default Filters;
